import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ColumnHeader } from './ColumnHeader';
import { ColumnBody } from './ColumnBody';
import { useIntersectionObserver } from 'react-intersection-observer-hook';
import { Loading } from 'components/Loading';
import { useService, requestCancelToken } from 'hooks/useService';
import { PublicNoticeAuction } from '../types';
import { Canceler } from 'axios';

interface PublicNoticeColumnProps {
    auction: PublicNoticeAuction;
}

interface ServiceResponse {
    content_html: string;
}

export const PublicNoticeColumn = ({ auction }: PublicNoticeColumnProps) => {
    const [showMoreContent, setShowMoreContent] = useState(false);
    const [showButton, setShowButton] = useState(false);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const cancelRef = useRef<Canceler | null>(null);

    const [
        {
            data: { content_html: html = '' },
            loading,
            error,
        },
        fireLoadHtml,
    ] = useService<ServiceResponse>({
        method: 'GET',
        url: `public-notices/auctions/${auction.auction_id}/content`,
        hasAuthedUrl: true,
    });

    const [containerRef, { entry }] = useIntersectionObserver({
        threshold: 0.5,
    });
    const isVisible = entry?.isIntersecting;

    const handleToggleContent = useCallback(() => {
        setShowMoreContent(prev => !prev);
    }, []);

    useEffect(() => {
        if (isVisible && !html && !loading && !error) {
            const { cancelCallback, cancelToken } = requestCancelToken();
            cancelRef.current = cancelCallback;

            fireLoadHtml({ cancelToken });
        }
    }, [isVisible, loading]);

    useEffect(() => {
        if (contentRef.current) {
            setShowButton(contentRef.current.offsetHeight > 400);
        }
    }, [html]);

    return (
        <div>
            <ColumnHeader>
                <div className="d-flex justify-content-between align-items-center w-100">
                    Public Notice
                </div>
            </ColumnHeader>

            <ColumnBody>
                {loading && !html && <Loading/>}
                {error && (
                    <div className="text-danger">
                        Failed to load Public Notice Content.
                    </div>
                )}

                <div
                    ref={containerRef}
                    className={`public-notice__html-body ${
                        showButton ? 'faded' : ''
                    } ${showMoreContent ? 'expanded' : ''} `}
                >
                    {html && (
                        <div
                            dangerouslySetInnerHTML={{ __html: html }}
                            ref={contentRef}
                        />
                    )}
                </div>

                {showButton && (
                    <button
                        className="public-notice__show-more-button"
                        onClick={handleToggleContent}
                    >
                        {showMoreContent ? 'Show Less -' : 'Show More +'}
                    </button>
                )}
            </ColumnBody>
        </div>
    );
};
